/** Shared form helpers for admin modals */

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';

export function slugify(input: string): string {
  const base = input
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\u0600-\u06FF-]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return base || `item-${Date.now()}`;
}

export const fieldClass =
  'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-right';

export const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

/** Persian/Arabic digits → Latin */
export function toEnDigits(input: string): string {
  return String(input ?? '')
    .replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(AR_DIGITS.indexOf(d)));
}

/** Keep only 0-9 after normalizing FA/AR digits */
export function onlyDigits(input: string): string {
  return toEnDigits(input).replace(/[^0-9]/g, '');
}

/** Parse money/qty from FA/EN digits, commas, and Persian separators */
export function parseNumber(input: string | number | null | undefined): number {
  if (typeof input === 'number') return Number.isFinite(input) ? input : 0;
  const cleaned = toEnDigits(String(input ?? ''))
    .replace(/,/g, '')
    .replace(/٬/g, '')
    .replace(/[^\d.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/** Admin display: Latin digits with thousand commas — 1,250,000 */
export function formatMoney(n: number | string | null | undefined): string {
  return new Intl.NumberFormat('en-US').format(parseNumber(n));
}

/** Alias for non-money counts that still need grouping */
export function formatGrouped(n: number | string | null | undefined): string {
  return formatMoney(n);
}

/** Admin datetime with Persian digits + Jalali calendar */
export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

/** Jalali calendar date for dashboard chart axis (YYYY-MM-DD or ISO datetime). */
export function formatJalaliDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  let d: Date;
  if (typeof value === 'string') {
    // Plain date → treat as Tehran calendar day noon UTC to avoid day shift.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      d = new Date(`${value}T12:00:00+03:30`);
    } else {
      d = new Date(value);
    }
  } else {
    d = value;
  }
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Jalali month label for YYYY-MM Gregorian month keys. */
export function formatJalaliMonth(value: string | null | undefined): string {
  if (!value) return '—';
  const m = value.match(/^(\d{4})-(\d{2})$/);
  if (!m) return formatJalaliDate(value);
  const d = new Date(`${m[1]}-${m[2]}-15T12:00:00+03:30`);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: 'long',
  }).format(d);
}
